/**
 * SPEC 4.8 — placeholder card variants for each primitive used during initial
 * data fetch. v1: a small set of generic shapes; per-surface variants will
 * extend in later weeks.
 */

type Variant = "line" | "block" | "card" | "row";

export function SkeletonLoader({
  variant = "block",
  width,
  height,
  count = 1,
}: {
  variant?: Variant;
  width?: number | string;
  height?: number | string;
  count?: number;
}) {
  const sizes = sizeFor(variant);
  const items = Array.from({ length: count });
  return (
    <div
      role="status"
      aria-label="Loading"
      aria-busy="true"
      style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}
    >
      {items.map((_, i) => (
        <span
          key={i}
          style={{
            display: "block",
            background:
              "linear-gradient(90deg, var(--chip) 0%, var(--chip-hi) 50%, var(--chip) 100%)",
            backgroundSize: "200% 100%",
            animation: "haus-skeleton-shimmer 1400ms linear infinite",
            borderRadius:
              variant === "card"
                ? "var(--radius-card)"
                : "var(--radius-chip)",
            width: width ?? sizes.width,
            height: height ?? sizes.height,
          }}
        />
      ))}
      <style>{`
        @keyframes haus-skeleton-shimmer {
          0% { background-position: 100% 0 }
          100% { background-position: -100% 0 }
        }
        @media (prefers-reduced-motion: reduce) {
          [aria-busy="true"] span { animation: none !important; }
        }
      `}</style>
    </div>
  );
}

function sizeFor(variant: Variant): { width: string; height: string } {
  switch (variant) {
    case "line":
      return { width: "60%", height: "12px" };
    case "block":
      return { width: "100%", height: "44px" };
    case "card":
      return { width: "100%", height: "120px" };
    case "row":
      return { width: "100%", height: "20px" };
  }
}
